import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle, Save, Loader2, Mail, Shield, CheckCircle2, AlertCircle, LogOut, X, Monitor } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useMDTEffects } from "../hooks/useMDTEffects";

export default function AreaProfilo() {
  const { user, refresh } = useAuth();
  const { effectsOn, toggle: toggleEffects } = useMDTEffects();
  const [discordId, setDiscordId] = useState("");
  const [saving, setSaving] = useState(false);
  // Dimissioni state
  const [pendingResignation, setPendingResignation] = useState(null);
  const [resignDialog, setResignDialog] = useState(false);
  const [resignReason, setResignReason] = useState("");
  const [resignLastDate, setResignLastDate] = useState("");
  const [resignSubmitting, setResignSubmitting] = useState(false);

  useEffect(() => {
    setDiscordId(user?.discord_id || "");
  }, [user]);

  useEffect(() => {
    // Recupera eventuale richiesta pending
    api.get("/resignations/me")
      .then(({ data }) => setPendingResignation(data || null))
      .catch(() => setPendingResignation(null));
  }, [user]);

  const handleSaveDiscord = async () => {
    if (!/^\d{15,25}$/.test(discordId)) {
      toast.error("ID Discord non valido (deve essere un numero di 15-25 cifre)");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/me/discord-id", { discord_id: discordId });
      toast.success("ID Discord aggiornato. Sincronizzazione ruolo eseguita.");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore aggiornamento ID Discord");
    } finally {
      setSaving(false);
    }
  };

  const hasDiscord = !!user?.discord_id;

  const submitResignation = async () => {
    if (resignReason.trim().length < 10) {
      toast.error("La motivazione deve essere di almeno 10 caratteri.");
      return;
    }
    setResignSubmitting(true);
    try {
      const { data } = await api.post("/resignations", {
        reason: resignReason.trim(),
        last_service_date: resignLastDate.trim() || null,
      });
      setPendingResignation(data);
      setResignDialog(false);
      setResignReason("");
      setResignLastDate("");
      toast.success("Richiesta dimissioni inviata. Riceverai l'esito via DM Discord dopo la valutazione HR.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setResignSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="area-profilo">
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <UserCircle className="h-4 w-4" /> Profilo Operatore
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#F5E6B8] mb-2">
          {user?.rp_name || "Operatore"}
        </h1>
        <p className="text-[#D4C480]">Gestisci le informazioni del tuo account e il collegamento Discord.</p>
      </div>

      {/* Info Account */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2 text-sheriff-gold/80 text-[10px] uppercase tracking-[0.15em] font-bold">
            <Mail className="h-3.5 w-3.5" /> Email
          </div>
          <div className="text-[#F5E6B8] font-mono text-sm">{user?.email}</div>
        </div>
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2 text-sheriff-gold/80 text-[10px] uppercase tracking-[0.15em] font-bold">
            <Shield className="h-3.5 w-3.5" /> Grado attuale
          </div>
          <div className="text-[#F5E6B8] font-heading font-bold">{user?.role || "—"}</div>
        </div>
      </div>

      {/* Preferenze MDT — toggle effetti CRT */}
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6 sm:p-8" data-testid="mdt-preferences">
        <div className="flex items-start gap-3 mb-4">
          <Monitor className="h-6 w-6 text-sheriff-gold shrink-0 mt-0.5" />
          <div>
            <h2 className="font-heading font-bold text-xl text-[#F5E6B8] mb-1">Preferenze terminale MDT</h2>
            <p className="text-sm text-[#B8A269]">Se trovi la lettura affaticante puoi disattivare l&apos;effetto scanline e il bagliore verde del CRT. La cornice Toughbook resta invariata.</p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 bg-[#0F2620] border border-sheriff-gold/25 rounded-lg px-4 py-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-sheriff-gold">Effetti CRT</div>
            <div className="text-sm text-[#F5E6B8]">Scanline + bagliore verde terminale</div>
            <div className="text-[10px] text-[#B8A269] mt-0.5">
              Stato attuale: <span className={effectsOn ? "text-emerald-300 font-bold" : "text-amber-300 font-bold"}>{effectsOn ? "ATTIVI" : "DISATTIVATI"}</span>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={effectsOn}
            onClick={toggleEffects}
            data-testid="mdt-effects-toggle"
            className={`relative inline-flex h-8 w-16 shrink-0 rounded-full border-2 transition-colors duration-200 ${
              effectsOn
                ? "border-emerald-400/50 bg-emerald-500/25"
                : "border-sheriff-gold/30 bg-[#0A1F18]"
            }`}
          >
            <span
              className={`inline-block h-6 w-6 rounded-full bg-sheriff-gold shadow transform transition-transform duration-200 ${
                effectsOn ? "translate-x-8" : "translate-x-0.5"
              }`}
            />
            <span className="sr-only">{effectsOn ? "Disattiva effetti CRT" : "Attiva effetti CRT"}</span>
          </button>
        </div>
      </div>

      {/* Discord ID */}
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-6 sm:p-8">
        <div className="flex items-start gap-3 mb-4">
          <svg className="h-6 w-6 text-[#5865F2] shrink-0 mt-0.5" viewBox="0 0 71 55" fill="currentColor">
            <path d="M60.1 4.9A58.5 58.5 0 0 0 45.4.4a.2.2 0 0 0-.2.1c-.6 1.1-1.3 2.6-1.8 3.8a54 54 0 0 0-16.2 0c-.5-1.2-1.2-2.7-1.8-3.8a.2.2 0 0 0-.2-.1A58.4 58.4 0 0 0 10.5 4.9a.2.2 0 0 0-.1.1A59.7 59.7 0 0 0 .2 45.6a.2.2 0 0 0 .1.2c6.3 4.6 12.4 7.4 18.4 9.2a.2.2 0 0 0 .2-.1c1.4-1.9 2.7-3.9 3.7-6a.2.2 0 0 0-.1-.3c-2-.7-3.8-1.6-5.6-2.6a.2.2 0 0 1 0-.3l1.1-.9a.2.2 0 0 1 .2 0c11.7 5.3 24.4 5.3 36 0a.2.2 0 0 1 .2 0l1.1.9a.2.2 0 0 1 0 .3c-1.8 1-3.6 1.9-5.6 2.6a.2.2 0 0 0-.1.3c1.1 2.1 2.3 4.1 3.7 6a.2.2 0 0 0 .2.1c6-1.8 12.1-4.6 18.4-9.2a.2.2 0 0 0 .1-.2A59.4 59.4 0 0 0 60.2 5a.2.2 0 0 0-.1-.1ZM23.7 37.3c-3.6 0-6.6-3.3-6.6-7.4 0-4.1 3-7.4 6.6-7.4 3.7 0 6.7 3.4 6.6 7.4 0 4.1-3 7.4-6.6 7.4Zm24.5 0c-3.6 0-6.6-3.3-6.6-7.4 0-4.1 3-7.4 6.6-7.4 3.7 0 6.7 3.4 6.6 7.4 0 4.1-2.9 7.4-6.6 7.4Z"/>
          </svg>
          <div>
            <h2 className="font-heading font-bold text-xl text-[#F5E6B8] mb-1">Collegamento Discord</h2>
            <p className="text-sm text-[#B8A269]">Inserisci il tuo ID Discord per sincronizzare automaticamente il grado quando vieni promosso sul sito.</p>
          </div>
        </div>

        {hasDiscord ? (
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3 mb-4 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" />
            <div className="text-sm">
              <span className="text-emerald-300 font-bold">Account collegato</span>
              <span className="text-[#D4C480] font-mono ml-2">ID: {user.discord_id}</span>
            </div>
          </div>
        ) : (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
            <span className="text-sm text-[#D4C480]">Nessun ID Discord configurato. La sincronizzazione del grado <span className="text-sheriff-gold font-bold">non è attiva</span>.</span>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sheriff-gold uppercase-label">ID Discord</Label>
          <div className="flex gap-2">
            <Input
              data-testid="profilo-discord-id"
              type="text"
              inputMode="numeric"
              value={discordId}
              onChange={(e) => setDiscordId(e.target.value.replace(/\D/g, ""))}
              placeholder="es. 351876452198482432"
              className="flex-1"
            />
            <Button
              onClick={handleSaveDiscord}
              disabled={saving || discordId === (user?.discord_id || "")}
              data-testid="save-discord-id"
              className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase-label"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Salva
            </Button>
          </div>
          <p className="text-[10px] text-[#8B7B49] leading-snug">
            Su Discord: <span className="text-sheriff-gold">Impostazioni utente → Avanzate → Modalità sviluppatore ON</span>. Poi click destro sul tuo nome → <span className="text-sheriff-gold">Copia ID utente</span>.
          </p>
        </div>
      </div>

      {/* SEZIONE DIMISSIONI */}
      <div className="rounded-2xl border border-rose-500/30 bg-[#1a0e0e] p-5 sm:p-6" data-testid="resignation-section">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-rose-500/15 border border-rose-500/40 flex items-center justify-center shrink-0">
            <LogOut className="h-5 w-5 text-rose-300" />
          </div>
          <div>
            <div className="uppercase-label text-[10px] text-rose-300/80">§ Dimissioni dal Dipartimento</div>
            <h2 className="text-lg font-heading font-bold text-[#F5E6B8] mt-1">Richiedi le dimissioni</h2>
            <p className="text-xs text-[#D4C480]/80 mt-1 max-w-2xl">
              Se hai deciso di lasciare il servizio, puoi inoltrare qui la tua richiesta ufficiale.
              Le Risorse Umane la valuteranno e ti risponderanno via DM Discord.
            </p>
          </div>
        </div>

        {pendingResignation ? (
          <div className="mt-3 p-4 rounded-lg bg-amber-500/10 border border-amber-400/40" data-testid="resignation-pending">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-heading font-bold uppercase-label">
              <AlertCircle className="h-4 w-4" /> Richiesta in attesa di valutazione
            </div>
            <div className="mt-2 text-xs text-[#D4C480]">
              Inviata il{" "}
              <span className="font-mono text-amber-200">
                {pendingResignation.requested_at ? new Date(pendingResignation.requested_at).toLocaleString("it-IT") : "—"}
              </span>
              . Aspetta la decisione HR: riceverai un DM del bot con l&apos;esito.
            </div>
            <div className="mt-2 text-xs text-[#8B7B49] italic">&ldquo;{pendingResignation.reason?.slice(0, 200)}{pendingResignation.reason?.length > 200 ? "…" : ""}&rdquo;</div>
          </div>
        ) : (
          <Button
            onClick={() => setResignDialog(true)}
            variant="ghost"
            className="text-rose-300 border border-rose-400/40 hover:bg-rose-500/10"
            data-testid="request-resignation-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Richiedi Dimissioni
          </Button>
        )}
      </div>

      {/* Modal richiesta */}
      <Dialog open={resignDialog} onOpenChange={(o) => !o && !resignSubmitting && setResignDialog(false)}>
        <DialogContent className="bg-[#0A1F18] border-rose-500/40 max-w-lg" data-testid="resignation-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-200 font-heading flex items-center gap-2">
              <LogOut className="h-5 w-5" /> Richiesta di Dimissioni
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[10px] uppercase-label text-rose-300 tracking-wider mb-1.5 block">Ultimo giorno di servizio (facoltativo)</Label>
              <Input
                value={resignLastDate}
                onChange={(e) => setResignLastDate(e.target.value)}
                placeholder="es. 20/07/2026"
                className="bg-[#0F2620] border-rose-400/30 text-[#F5E6B8]"
                data-testid="resignation-last-date"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase-label text-rose-300 tracking-wider mb-1.5 block">Motivazione * (min 10 caratteri)</Label>
              <Textarea
                value={resignReason}
                onChange={(e) => setResignReason(e.target.value)}
                placeholder="Spiega brevemente il motivo della richiesta di dimissioni…"
                rows={5}
                className="bg-[#0F2620] border-rose-400/30 text-[#F5E6B8]"
                data-testid="resignation-reason"
              />
              <div className="text-[10px] text-[#8B7B49] mt-1 text-right">{resignReason.length}/2000</div>
            </div>
            <div className="text-[11px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
              ⚠️ Una volta inviata, la richiesta viene notificata su Discord e sarà visionata dalle Risorse Umane.
              Se approvata, ti verrà richiesto di presentarti in servizio per la consegna dell&apos;equipaggiamento.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResignDialog(false)} disabled={resignSubmitting} className="text-[#D4C480]">
              Annulla
            </Button>
            <Button
              onClick={submitResignation}
              disabled={resignSubmitting || resignReason.trim().length < 10}
              className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
              data-testid="submit-resignation"
            >
              {resignSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio…</> : <><LogOut className="h-4 w-4 mr-2" /> Invia Richiesta</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
